import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageChannels, channelLabel } from '@aiinterview/shared';
import { api, ApiError } from '../api/client';
import {
    Button, Card, Field, TextInput, TextArea, Select, Loading, ErrorNote, toast
} from '../components/ui';
import {
    MessagePreview, SAMPLE_VALUES, fillPlain, messagesIn, metaTemplateName
} from '../components/TemplatePreview';

/**
 * Pointing the product at an SMS gateway and at WhatsApp.
 *
 * A tab under Settings, beside Branding and Security, rather than a sidebar entry of its own: it is
 * configuration and it is read once during setup, not a place anybody works. It gets its own
 * component instead of being rendered generically from the settings catalogue, because setting a
 * gateway up is a sitting with the vendor's documentation open beside you - copying an endpoint,
 * pasting a request body, sending a test, reading what came back, fixing it. The presets and the
 * test send are what make that bearable, and a generic field list cannot carry either.
 *
 * The two channels are configured independently, credentials included, because they are normally
 * bought from different companies: an Indian gateway for text, Meta directly for WhatsApp.
 */
interface SettingRow {
    key: string;
    dataType: string;
    description: string;
    displayOrder: number;
    value: string | null;
    isConfigured: boolean | null;
}

interface TestResult {
    sent: boolean;
    to: string;
    error: string | null;
    providerRef: string | null;
    /** The gateway's own status code and reply, shown only here. */
    status: number | null;
    body: string;
}

const KEY = {
    countryCode: 'Provider.Messaging.CountryCode',
    failureMarker: 'Provider.Messaging.FailureMarker',

    smsUrl: 'Provider.Messaging.SmsUrl',
    smsMethod: 'Provider.Messaging.SmsMethod',
    smsHeaders: 'Provider.Messaging.SmsHeaders',
    smsUserName: 'Provider.Messaging.SmsUserName',
    // Snyk reads this as a hardcoded secret. It is a setting key name, not a value; the value it
    // addresses is write-only on screen and never sent back to a browser.
    smsApiKey: 'Provider.Messaging.SmsApiKey',
    smsSenderId: 'Provider.Messaging.SmsSenderId',
    smsBody: 'Provider.Messaging.SmsBody',

    whatsappUrl: 'Provider.Messaging.WhatsappUrl',
    whatsappMethod: 'Provider.Messaging.WhatsappMethod',
    whatsappHeaders: 'Provider.Messaging.WhatsappHeaders',
    // Snyk reads this as a hardcoded secret. It is a setting key name, not a value; the value it
    // addresses is write-only on screen and never sent back to a browser.
    whatsappApiKey: 'Provider.Messaging.WhatsappApiKey',
    whatsappBody: 'Provider.Messaging.WhatsappBody'
} as const;

/**
 * Meta's WhatsApp Cloud API, filled in for you.
 *
 * The one gateway whose exact shape is worth shipping as a preset: it is identical for every
 * business, Meta document it publicly, and the alternative is somebody transcribing nested JSON by
 * hand from a docs page. Two things still have to be replaced afterwards and the preset names them
 * in capitals - the phone number ID in the endpoint, and the approved template name in the body.
 *
 * The API version is the first thing to check if a test send is rejected: Meta raise it several
 * times a year and retire the old ones.
 */
const META_PRESET = {
    url: 'https://graph.facebook.com/v21.0/PHONE_NUMBER_ID/messages',
    method: 'POST',
    headers: '{"Content-Type":"application/json","Authorization":"Bearer {{apiKey}}"}',
    body: JSON.stringify({
        messaging_product: 'whatsapp',
        to: '{{mobile}}',
        type: 'template',
        template: {
            name: 'YOUR_TEMPLATE_NAME',
            language: { code: 'en' },
            components: [
                { type: 'body', parameters: [{ type: 'text', text: '{{link}}' }] }
            ]
        }
    }, null, 2)
};

/**
 * apiconnecto.com, filled in for you.
 *
 * Taken from a working integration rather than guessed: everything goes in the query string,
 * credentials included, and there is no request body at all.
 *
 * The endpoint is http, not https - that is what apiconnecto publish, and it means the username,
 * the password and the message all cross the network in clear text. Worth asking them for a TLS
 * endpoint; if they have one, only the scheme here needs changing.
 */
const APICONNECTO_PRESET = {
    url: 'http://www.apiconnecto.com/API/SMSApiConnector.aspx'
        + '?UserId={{username}}&pwd={{password}}'
        + '&Message=Please share your feedback: {{link}}'
        + '&Contacts={{mobile}}&SenderId={{senderId}}'
        + '&ServiceName=SMSTRANS&MessageType=1',
    method: 'GET',
    headers: '{}',
    body: ''
};

/** What can go in an endpoint, a header or a body, and what each one becomes. */
const PLACEHOLDERS: [string, string][] = [
    ['{{username}}', 'The account username, where the gateway uses one'],
    ['{{password}}', 'The stored secret. {{apiKey}} is the same value under its other name'],
    ['{{senderId}}', 'The sender ID — text messages only'],
    ['{{mobile}}', 'The number, digits only, with the country code'],
    ['{{link}}', 'The survey or listening link — the point of the message'],
    ['{{campaign}}', 'The name of the survey or listening session'],
    ['{{employeeCode}}', 'Who it is going to']
];

export function Messaging() {
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [error, setError] = useState<ApiError | null>(null);
    const [testChannel, setTestChannel] = useState<string>(MessageChannels.Sms);
    const [testNumber, setTestNumber] = useState('');
    const [result, setResult] = useState<TestResult | null>(null);
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get<{ settings: SettingRow[] }>('/api/admin/settings?category=Messaging')
    });

    const save = useMutation({
        mutationFn: () => api.post('/api/admin/settings', { values: edits }),
        onSuccess: async () => {
            toast('Saved.');
            setEdits({});
            setError(null);
            await queryClient.invalidateQueries({ queryKey: ['settings'] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    const test = useMutation({
        mutationFn: () => api.post<TestResult>('/api/admin/settings/test-message', {
            channel: testChannel, to: testNumber
        }),
        onSuccess: (outcome) => {
            setResult(outcome);
            toast(outcome.sent ? 'The gateway accepted it.' : 'The gateway refused it.',
                outcome.sent ? 'success' : 'error');
        },
        onError: (caught: ApiError) => { setResult(null); toast(caught.message, 'error'); }
    });

    if (isLoading || !data) return <Loading what="the gateway settings" />;

    const rows = new Map(data.settings.map((s) => [s.key, s]));
    const row = (key: string) => rows.get(key);
    const valueOf = (key: string) => edits[key] ?? row(key)?.value ?? '';
    const set = (key: string, value: string) => setEdits((e) => ({ ...e, [key]: value }));
    const hint = (key: string) => row(key)?.description;
    const secretStored = (key: string) => row(key)?.isConfigured === true;

    const unsaved = Object.keys(edits).length > 0;
    const smsOn = valueOf(KEY.smsUrl).trim() !== '';
    const whatsappOn = valueOf(KEY.whatsappUrl).trim() !== '';

    const shared = { valueOf, set, hint, secretStored, error, edits };

    return (
        <div className="space-y-6">
            <Card
                title="SMS &amp; WhatsApp"
                lede="Where text messages and WhatsApp invitations are sent from. The two are set up
                      separately, credentials included, because they usually come from different
                      companies. Email invitations use neither — they go through the email provider
                      in Settings."
                action={(
                    <Button busy={save.isPending} disabled={!unsaved} onClick={() => save.mutate()}>
                        Save
                    </Button>
                )}
            >
                {error && !error.errors && <ErrorNote message={error.message} />}

                {/* A channel is on when it has an endpoint. Said here rather than left to be
                    inferred from an empty box further down the page. */}
                <div className="mb-5 flex flex-wrap gap-2">
                    <ChannelPill label="Text messages" on={smsOn} />
                    <ChannelPill label="WhatsApp" on={whatsappOn} />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Country code" hint={hint(KEY.countryCode)}>
                        <TextInput
                            value={valueOf(KEY.countryCode)}
                            onChange={(e) => set(KEY.countryCode, e.target.value)}
                        />
                    </Field>

                    <Field label="Treat as a failure" hint={hint(KEY.failureMarker)}>
                        <TextInput
                            value={valueOf(KEY.failureMarker)}
                            onChange={(e) => set(KEY.failureMarker, e.target.value)}
                            placeholder="error,failed,invalid"
                        />
                    </Field>
                </div>
            </Card>

            <Card
                title="What goes in a request"
                lede="Copy the endpoint and the request body straight out of your gateway's
                      documentation, then swap their example values for these."
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <tbody>
                            {PLACEHOLDERS.map(([token, meaning]) => (
                                <tr key={token} className="border-b border-slate-100 last:border-0">
                                    <td className="py-1.5 pr-4 font-mono text-xs text-slate-800">
                                        {token}
                                    </td>
                                    <td className="py-1.5 text-slate-600">{meaning}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="mt-3 text-xs text-slate-500">
                    Values are escaped for wherever they land — quoted correctly inside a JSON body,
                    URL-encoded inside an endpoint or a form body. You do not need to escape anything
                    yourself.
                </p>
            </Card>

            <ChannelCard
                {...shared}
                title="Text messages"
                on={smsOn}
                urlKey={KEY.smsUrl}
                methodKey={KEY.smsMethod}
                headersKey={KEY.smsHeaders}
                apiKeyKey={KEY.smsApiKey}
                senderIdKey={KEY.smsSenderId}
                bodyKey={KEY.smsBody}
                userNameKey={KEY.smsUserName}
                secretLabel="Password or API key"
                placeholderUrl="https://api.example.com/v1/sms/send"
                placeholderBody={'{"sender":"{{senderId}}","number":"{{mobile}}",'
                    + '"message":"Your survey link: {{link}}"}'}
                preset={{
                    label: 'Fill in apiconnecto’s format',
                    note: 'apiconnecto.com authenticates with a username and password rather than '
                        + 'a key, and takes everything in the query string. Fill in the username, '
                        + 'password and sender ID below, then edit the Message text in the endpoint '
                        + 'to match your DLT-approved template.',
                    apply: () => setEdits((e) => ({
                        ...e,
                        [KEY.smsUrl]: APICONNECTO_PRESET.url,
                        [KEY.smsMethod]: APICONNECTO_PRESET.method,
                        [KEY.smsHeaders]: APICONNECTO_PRESET.headers,
                        [KEY.smsBody]: APICONNECTO_PRESET.body
                    }))
                }}
            />

            <ChannelCard
                {...shared}
                title="WhatsApp"
                on={whatsappOn}
                urlKey={KEY.whatsappUrl}
                methodKey={KEY.whatsappMethod}
                headersKey={KEY.whatsappHeaders}
                apiKeyKey={KEY.whatsappApiKey}
                bodyKey={KEY.whatsappBody}
                secretLabel="Access token"
                placeholderUrl="https://graph.facebook.com/v21.0/<phone-number-id>/messages"
                placeholderBody={META_PRESET.body}
                preset={{
                    label: 'Fill in Meta’s format',
                    note: 'For a WhatsApp Business Account through Meta directly. This fills in the '
                        + 'endpoint, headers and request body; replace PHONE_NUMBER_ID and '
                        + 'YOUR_TEMPLATE_NAME with your own, and paste your System User token as '
                        + 'the access token.',
                    apply: () => setEdits((e) => ({
                        ...e,
                        [KEY.whatsappUrl]: META_PRESET.url,
                        [KEY.whatsappMethod]: META_PRESET.method,
                        [KEY.whatsappHeaders]: META_PRESET.headers,
                        [KEY.whatsappBody]: META_PRESET.body
                    }))
                }}
            />

            <Card
                title="Send a test"
                lede="Uses whatever is saved above, to a real number. The gateway's own reply is
                      shown underneath — which is usually the quickest way to find out what it
                      actually wants."
            >
                <div className="flex flex-wrap items-end gap-3">
                    <Field label="How">
                        <Select value={testChannel} onChange={(e) => setTestChannel(e.target.value)}>
                            <option value={MessageChannels.Sms}>
                                {channelLabel(MessageChannels.Sms)}
                            </option>
                            <option value={MessageChannels.Whatsapp}>
                                {channelLabel(MessageChannels.Whatsapp)}
                            </option>
                        </Select>
                    </Field>

                    <Field label="To this number">
                        <TextInput
                            value={testNumber}
                            onChange={(e) => setTestNumber(e.target.value)}
                            placeholder="9876543210"
                        />
                    </Field>

                    <Button
                        variant="secondary"
                        busy={test.isPending}
                        disabled={testNumber.trim() === '' || unsaved}
                        onClick={() => test.mutate()}
                    >
                        Send it
                    </Button>
                </div>

                {unsaved && (
                    <p className="mt-3 text-sm text-amber-700">
                        Save first — the test uses what is stored, not what is on screen.
                    </p>
                )}

                {result && <TestReport result={result} />}
            </Card>
        </div>
    );
}

const ChannelPill = ({ label, on }: { label: string; on: boolean }) => (
    <span
        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
            on ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}
    >
        {label}: {on ? 'on' : 'off'}
    </span>
);

interface SharedProps {
    valueOf: (key: string) => string;
    set: (key: string, value: string) => void;
    hint: (key: string) => string | undefined;
    secretStored: (key: string) => boolean;
    error: ApiError | null;
    edits: Record<string, string>;
}

/**
 * One channel, end to end: where it sends, how it authenticates, and what it sends.
 *
 * Both channels get the same component because they take the same settings - but two instances with
 * two sets of keys, never one shared set. That separation is the point: an SMS gateway key and a
 * Meta access token belong to different companies and must not sit in the same field.
 */
function ChannelCard({
    title, on, urlKey, methodKey, headersKey, apiKeyKey, userNameKey, senderIdKey, bodyKey,
    secretLabel, placeholderUrl, placeholderBody, preset, valueOf, set, hint, secretStored, error,
    edits
}: SharedProps & {
    title: string;
    on: boolean;
    urlKey: string;
    methodKey: string;
    headersKey: string;
    apiKeyKey: string;
    userNameKey?: string;
    senderIdKey?: string;
    bodyKey: string;
    secretLabel: string;
    placeholderUrl: string;
    placeholderBody: string;
    preset?: { label: string; note: string; apply: () => void };
}) {
    const method = valueOf(methodKey) || 'POST';
    // The one place the two channels differ in behaviour rather than in labels: a WhatsApp
    // request carries template parameters, not a message, so its preview says so.
    const isWhatsapp = title === 'WhatsApp';

    return (
        <Card
            title={title}
            lede={on
                ? undefined
                : 'No endpoint, so this channel is switched off. Anyone sent on it is recorded as '
                    + 'not sent, with the reason.'}
            action={preset && (
                <Button variant="secondary" onClick={preset.apply}>{preset.label}</Button>
            )}
        >
            {preset && <p className="mb-4 text-sm text-slate-500">{preset.note}</p>}

            <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
                <Field label="Endpoint" hint={hint(urlKey)}>
                    <TextInput
                        value={valueOf(urlKey)}
                        onChange={(e) => set(urlKey, e.target.value)}
                        placeholder={placeholderUrl}
                    />
                </Field>

                <Field label="Method">
                    <Select value={method} onChange={(e) => set(methodKey, e.target.value)}>
                        <option value="POST">POST</option>
                        <option value="GET">GET</option>
                    </Select>
                </Field>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {userNameKey && (
                    <Field label="Username" hint={hint(userNameKey)}>
                        <TextInput
                            value={valueOf(userNameKey)}
                            onChange={(e) => set(userNameKey, e.target.value)}
                        />
                    </Field>
                )}

                <Field
                    label={secretLabel}
                    hint="Stored encrypted and never shown again. Leave blank to keep the one already
                          saved."
                    error={error?.fieldError(apiKeyKey)}
                >
                    <div className="flex items-center gap-3">
                        <TextInput
                            type="password"
                            autoComplete="new-password"
                            value={edits[apiKeyKey] ?? ''}
                            onChange={(e) => set(apiKeyKey, e.target.value)}
                            placeholder={secretStored(apiKeyKey)
                                ? 'Stored. Type a new one to replace it.'
                                : 'Not set'}
                        />
                        <span
                            className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                secretStored(apiKeyKey)
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-slate-100 text-slate-600'}`}
                        >
                            {secretStored(apiKeyKey) ? 'Stored' : 'Not set'}
                        </span>
                    </div>
                </Field>

                {senderIdKey && (
                    <Field label="Sender ID" hint={hint(senderIdKey)}>
                        <TextInput
                            value={valueOf(senderIdKey)}
                            onChange={(e) => set(senderIdKey, e.target.value)}
                        />
                    </Field>
                )}
            </div>

            <div className="mt-4">
                <Field label="Headers" hint={hint(headersKey)}>
                    <TextArea
                        rows={2}
                        className="font-mono text-xs"
                        value={valueOf(headersKey)}
                        onChange={(e) => set(headersKey, e.target.value)}
                    />
                </Field>
            </div>

            <div className="mt-4">
                <Field
                    label="Request body"
                    hint={method === 'GET'
                        ? 'Not sent for GET — put everything in the endpoint above instead.'
                        : hint(bodyKey)}
                >
                    <TextArea
                        rows={8}
                        className="font-mono text-xs"
                        disabled={method === 'GET'}
                        value={valueOf(bodyKey)}
                        onChange={(e) => set(bodyKey, e.target.value)}
                        placeholder={placeholderBody}
                    />
                </Field>
            </div>

            <div className="mt-5">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    What it will look like
                </h3>
                <ChannelPreview
                    url={valueOf(urlKey)}
                    body={method === 'GET' ? '' : valueOf(bodyKey)}
                    whatsapp={isWhatsapp}
                />
            </div>
        </Card>
    );
}

/**
 * The message as a handset will show it, built from whatever is on screen.
 *
 * Follows the unsaved edits rather than the stored values, because the point is to see the wording
 * you are currently typing. The credentials resolve to dots: a preview is the most screenshotted
 * thing on a settings page, and there is no version of this that needs a live password in it.
 */
function ChannelPreview({ url, body, whatsapp }: {
    url: string;
    body: string;
    whatsapp: boolean;
}) {
    const found = messagesIn(url, body).map((text) => fillPlain(text, SAMPLE_VALUES));
    const template = whatsapp ? metaTemplateName(body) : null;

    if (found.length === 0) {
        return (
            <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                No message text found yet. Fill in the endpoint or the request body above — the
                preview reads whichever of them carries the message.
            </p>
        );
    }

    return (
        <MessagePreview
            channel={whatsapp ? 'Whatsapp' : 'Sms'}
            text={found.join('\n')}
            note={whatsapp
                ? `These are the values your template${template ? ` “${template}”` : ''} will `
                    + 'receive. The wording around them lives in the template Meta approved, not '
                    + 'here, so the real message will have more text than this.'
                : 'Sample values shown. The employee sees their own link, and the credentials are '
                    + 'never part of the message.'}
        />
    );
}

function TestReport({ result }: { result: TestResult }) {
    return (
        <div className="mt-5 space-y-2 border-t border-slate-200 pt-4">
            <p className={`text-sm font-medium ${result.sent ? 'text-emerald-700' : 'text-red-700'}`}>
                {result.sent
                    ? `The gateway accepted it for ${result.to}.`
                    : `The gateway did not accept it for ${result.to}.`}
            </p>

            {result.error && <p className="text-sm text-slate-600">{result.error}</p>}

            {result.providerRef && (
                <p className="text-xs text-slate-500">
                    Their reference: <span className="font-mono">{result.providerRef}</span>
                </p>
            )}

            {result.body && (
                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        What it replied {result.status !== null && `(HTTP ${result.status})`}
                    </h3>
                    <pre className="mt-1 overflow-x-auto rounded bg-slate-50 p-3 font-mono text-xs text-slate-700">
                        {result.body}
                    </pre>
                </div>
            )}

            <p className="text-xs text-slate-500">
                Accepted means the gateway took it, not that it arrived. Check the handset.
            </p>
        </div>
    );
}
